import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IContext } from '@context/models/context';
import { select, Store } from '@ngrx/store';
import { ApplicationState } from '@feature/store/application-store';
import * as paymentsActions from '@feature/payments-to-you/actions/payments-to-you-table';
import * as paymentsColumnActions from '@feature/payments-to-you/actions/payments-to-you-table-columns';
import { DashboardItem } from '@dashboards/models/dashboard-item';
import { ITable, IResizedColumn } from '@common/models/table';
import * as paymentToYouTableSelectors from '@payments-to-you/selectors/payments-table';
import * as paymentToYouTableColumnSelectors from '@payments-to-you/selectors/payments-table-columns';
import { IColumnDefinition } from '@common/models/column-definition';
import { RuleSet, QueryBuilderConfig } from 'ngx-angular-query-builder';
import { PaymentsToYouHttpService } from '@feature/payments-to-you/services/payments-to-you-http.service';
import { RemittanceDocument } from '@feature/payments-to-you/models/remittance-document';

@Injectable()
export class PaymentsToYouService {

    constructor(
      private readonly store: Store<ApplicationState>,
      private readonly paymentsToYouHttpService: PaymentsToYouHttpService
 ) {
    }

    reorderColumns(widget: DashboardItem, selectedColumns: string[]) {
        this.store.dispatch(paymentsColumnActions.reorderColumns({ payload: selectedColumns, widget: widget }));
    }

    resizedColumns(widget: DashboardItem, resizedColumn: IResizedColumn) {
      this.store.dispatch(paymentsColumnActions.resizeColumns({ payload: resizedColumn, widget: widget }));
    }

    updateMojoColumns(widget: DashboardItem, columns: IColumnDefinition[], context: IContext) {
        this.store.dispatch(paymentsColumnActions.updateMojoColumn({ context, columnDefinition: columns, widget: widget }));
    }

    updateMfsColumns(widget: DashboardItem, columns: IColumnDefinition[], accountRef: number) {
        this.store.dispatch(paymentsColumnActions.updateMfsColumn({ accountRef, columnDefinition: columns, widget: widget }));
    }

    resetMojoColumns(widget: DashboardItem, context: IContext) {
      this.store.dispatch(paymentsColumnActions.resetToMojoDefault({ context, widget: widget }));
    }

    resetMfsColumns(widget: DashboardItem, accountRef: number) {
      this.store.dispatch(paymentsColumnActions.resetToMfsDefault({ accountRef, widget: widget }));
    }

    loadInternalPayments(widget: DashboardItem, context: IContext) {
        this.store.dispatch(paymentsActions.loadInternal({ context: context, widget: widget }));
    }

    loadExternalPayments(widget: DashboardItem, accountRef: number = undefined) {
        this.store.dispatch(paymentsActions.loadExternal({ accountRef: accountRef, widget: widget }));
    }

    getTable(widget: DashboardItem): Observable<ITable> {
        return this.store.pipe(select(paymentToYouTableSelectors.getFilteredPaymentsWithDataKey(widget.id.toString(), widget.widgetConfigId, paymentToYouTableSelectors.makePaymentFilterFieldProviderCallback())));
    }

    getNoOfNewPayments(widget: DashboardItem, previousLoginTime: Date): Observable<number> {
      return this.store.pipe(select(paymentToYouTableSelectors.getNoOfNewPayments(widget.id.toString(), previousLoginTime)));
    }

    loadColumns(widget: DashboardItem) {
        this.store.dispatch(paymentsColumnActions.load({widget}));
    }

    loadAllColumns() {
      this.store.dispatch(paymentsColumnActions.loadAll());
    }

    getAllColumns() {
      return this.store.pipe(select(paymentToYouTableColumnSelectors.getAllColumns()));
    }

    getColumns(widget: DashboardItem): Observable<IColumnDefinition[]> {
      return this.store.pipe(select(paymentToYouTableColumnSelectors.getAvailableColumns(widget.id.toString())));
    }

    getUserFilter(widget: DashboardItem): Observable<RuleSet> {
      return this.store.pipe(select(paymentToYouTableSelectors.getUserFilter(widget.id.toString())));
    }

    getQueryConfig(widget: DashboardItem): Observable<QueryBuilderConfig> {

        const callbackFunction = paymentToYouTableSelectors.makePaymentFilterFieldProviderCallback();
        return this.store.pipe(
          select(paymentToYouTableSelectors.getQueryBuilderConfig(widget.id.toString(), callbackFunction)));
  }

  getRemittanceDocument(cashBookUri: number): Observable<RemittanceDocument> {
    return this.paymentsToYouHttpService.getRemittanceDocument(cashBookUri);
  }

}
